import { resolveApiBaseUrl } from '../config/apiBaseUrl';
import { getApiLanguage } from '../config/apiLanguage';
import { isSubscriptionLimitTitle } from '../utils/subscription';
import { cachedFetch, clearApiCache, invalidateApiCache, peekApiCache, setApiCache } from './apiCache';
import {
  isUpdateBusinessIosFailureEnvelope,
  UpdateBusinessIosEnvelope,
} from './businessIosEnvelope';

// API service for backend communication
// Uses the same endpoints as the iOS app

const API_BASE_URL = resolveApiBaseUrl();

export { clearApiCache, invalidateApiCache };

const TTL = {
  categories: 30 * 60_000,
  onlyUserCategories: 10 * 60_000,
  userCategories: 2 * 60_000,
  upNext: 60_000,
  folders: 60_000,
  favorites: 45_000,
  search: 30_000,
  searchCategories: 60_000,
  searchHistory: 60_000,
} as const;

function roundCoord(n?: string | number): string {
  if (n == null || n === '') return '';
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '';
  // Match backend home-feed geo bucket (~1km) so client + server share hit rates.
  return v.toFixed(2);
}

/** Stable cache actor — survives JWT refresh (same sub/user_id). */
function cacheActorKey(token?: string): string {
  if (!token) return 'anon';
  try {
    const part = token.split('.')[1];
    if (!part) return 'auth';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    const id = payload.sub ?? payload.user_id ?? payload.id;
    if (id != null && String(id).length > 0) return `u${id}`;
  } catch {
    /* ignore */
  }
  return 'auth';
}

const jwtJsonHeaders = (token: string) =>
  ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }) as Record<string, string>;

export interface ApiBusiness {
  id: number;
  username: string;
  name: string;
  logo?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  /** Platform-wide save count returned by /upNext, used to rank Up next. */
  save_count?: number;
  category?: {
    id: number;
    name: string;
    icon?: string;
  };
  subcategory?: {
    id: number;
    name: string;
    icon?: string;
  };
  subsubcategory?: {
    id: number;
    name: string;
  };
  addresses?: Array<{
    id: number;
    latitude: number;
    longitude: number;
    address?: string;
    city?: string;
    country?: string;
  }>;
  emails?: Array<{
    id: number;
    email: string;
  }>;
  phones?: Array<{
    id?: number;
    phone: string;
    extras?: Array<{
      id?: number;
      name?: string;
      extra?: string;
      phone_id?: number;
      selected?: number;
    }>;
  }>;
  covers?: Array<{
    id: number;
    cover: string;
  }>;
  socials?: Array<{
    id?: number;
    social?: string;
    name?: string;
    url?: string;
    type?: {
      id?: number;
      name: string;
      icon?: string;
      preweb?: string;
      postweb?: string;
      prelink?: string;
      postlink?: string;
    };
  }>;
  distance?: number;
  favorite?: number;
  coverStrings?: string;
  coverSize?: number;
  video?: string;
  web?: string;
  active?: number;
  place_type_id?: number;
  placetype?: {
    id: number;
    name: string;
  };
  main_business?: {
    business?: {
      name?: string;
    };
  };
}

export interface ApiFeedResponse {
  businesses: ApiBusiness[];
  categories: Array<{
    id: number;
    name: string;
    icon?: string;
    subcategories?: Array<{
      id: number;
      name: string;
      icon?: string;
    }>;
  }>;
  subcategories: Array<{
    id: number;
    name: string;
    icon?: string;
    category_id?: number;
  }>;
}

export interface ApiSingleBusinessResponse {
  business: ApiBusiness;
}

/**
 * Fetch businesses from the feed endpoint
 * @param lat Optional latitude for distance calculation
 * @param lng Optional longitude for distance calculation
 * @param token Optional authentication token
 */
export async function fetchFeed(lat?: number, lng?: number, token?: string): Promise<ApiFeedResponse> {
  const params = new URLSearchParams();
  if (lat !== undefined) params.append('lat', lat.toString());
  if (lng !== undefined) params.append('lng', lng.toString());
  if (token) params.append('token', token);
  params.append('language', getApiLanguage());

  const url = `${API_BASE_URL}/api/v1.1/feed${params.toString() ? `?${params.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch feed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch a single business by username
 * @param username Business username (without @)
 * @param token Authentication token (required for v1.1 endpoint)
 * @note The v1.1/singleBusiness endpoint requires JWT authentication
 * If you don't have a token, this may fail. Consider using the feed endpoint instead.
 */
export async function fetchSingleBusiness(username: string, token?: string): Promise<ApiSingleBusinessResponse> {
  const params = new URLSearchParams();
  params.append('username', username);
  params.append('language', getApiLanguage());
  if (token) params.append('token', token);

  const url = `${API_BASE_URL}/api/v1.1/singleBusiness?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Authentication required. Please log in to view business details.');
    }
    throw new Error(`Failed to fetch business: ${response.statusText}`);
  }

  const data = await response.json();

  const errorMessage =
    typeof data.message === 'string'
      ? data.message
      : Array.isArray(data.message) && typeof data.message[0] === 'string'
        ? data.message[0]
      : Array.isArray(data) && typeof data[0] === 'string'
        ? data[0]
        : null;

  // Stale JWTs must not block public profile reads — retry once without token.
  if (token && errorMessage && /token_expired|no_token|invalid_token/i.test(errorMessage) && !data.business) {
    return fetchSingleBusiness(username);
  }

  // Check if response contains an error message instead of business data
  if (errorMessage && !data.business) {
    throw new Error(
      errorMessage === 'no_token'
        ? 'Authentication required. Please log in to view business details.'
        : errorMessage
    );
  }

  // Check if business exists in response
  if (!data.business) {
    throw new Error('Business not found');
  }

  return data;
}

/**
 * Fetch related businesses (same user)
 * @param businessId Business ID
 * @param userId User ID (optional, for favorites)
 * @param token Authentication token (required)
 */
export async function fetchRelatedBusinesses(
  businessId: number,
  userId?: number,
  token?: string
): Promise<{ businesses: ApiBusiness[] }> {
  const params = new URLSearchParams();
  params.append('id', businessId.toString());
  if (userId) params.append('user_id', userId.toString());
  if (token) params.append('token', token);
  params.append('language', getApiLanguage());

  const url = `${API_BASE_URL}/api/v1.1/getRelatedBusinesses?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch related businesses: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get the full URL for a business logo
 */
function encodeAssetPath(name: string): string {
  return name
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
}

/**
 * Get the full URL for a business logo
 */
export function getBusinessLogoUrl(logo?: string): string | null {
  if (!logo) return null;
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${API_BASE_URL}/assets/img/site/clients/logos/${encodeAssetPath(logo)}`;
}

/**
 * Get the full URL for a business cover image
 */
export function getBusinessCoverUrl(cover?: string): string | null {
  if (!cover) return null;
  if (/^https?:\/\//i.test(cover)) return cover;
  return `${API_BASE_URL}/assets/covers/site/clients/covers/${encodeAssetPath(cover)}`;
}

export { getBusinessVideoUrl, getSocialIconUrl } from '../utils/assetUrls';

/**
 * Get the full URL for a category icon
 */
export function getCategoryIconUrl(icon?: string): string | null {
  if (!icon) return null;
  const trimmed = icon.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Already a path under assets/
  if (trimmed.startsWith('/assets/') || trimmed.startsWith('assets/')) {
    return `${API_BASE_URL}/${trimmed.replace(/^\//, '')}`;
  }
  // Filename or relative path under categoryimages/
  const file = trimmed.replace(/^categoryimages\//, '').replace(/^\//, '');
  return `${API_BASE_URL}/assets/categoryimages/${encodeURIComponent(file)}`;
}

/**
 * Fetch all categories with language support
 * Matches iOS: /api/v1.1/categories?language={lang}&token={token}
 */
export async function fetchCategories(language: string = getApiLanguage(), token?: string): Promise<{
  categories: Array<{
    id: number;
    name: string;
    image: string;
    color: string;
    favorite: number; // 0 or 1
    subcategories?: Array<{
      id: number;
      name: string;
      image?: string;
      subsubcategories?: Array<{
        id: number;
        name: string;
      }>;
    }>;
  }>;
}> {
  // Include token — response embeds per-user `favorite` flags.
  const key = `categories:${language}:${cacheActorKey(token)}`;
  return cachedFetch(
    key,
    async () => {
      const params = new URLSearchParams();
      params.append('language', language);
      if (token) params.append('token', token);
      const response = await fetch(`${API_BASE_URL}/api/v1.1/categories?${params.toString()}`, {
        method: 'GET',
        headers: token
          ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
          : { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to fetch categories: ${response.statusText}`);
      return response.json();
    },
    { ttlMs: TTL.categories, persist: 'local', staleWhileRevalidate: true, staleTtlMs: 2 * 60 * 60_000 }
  );
}

export const HOME_FEED_PAGE_SIZE = 15;

/**
 * Fetch user's selected categories, subcategories, and subsubcategories
 * Also returns businesses feed
 * Matches iOS: /api/v1.1/getUserCategories?language={lang}&token={token}&lat={lat}&lng={lng}&place_type={type}
 * Optional page/limit enables progressive home feed loading (limit omitted = full list).
 */
export async function fetchUserCategories(
  language: string = getApiLanguage(), 
  token?: string,
  lat?: string,
  lng?: string,
  placeType?: number,
  subcategoryIds?: number[],
  options?: { page?: number; limit?: number }
): Promise<{
  categories?: Array<{
    id: number;
    name: string;
    icon?: string;
    image?: string;
    color?: string;
    active?: number;
    [key: string]: any;
  }>;
  subcategories?: Array<{
    id: number;
    name: string;
    icon?: string;
    image?: string;
    category_id?: number;
    [key: string]: any;
  }>;
  subsubcategories?: Array<{
    id: number;
    name: string;
    category_id?: number;
    subcategory_id?: number;
    [key: string]: any;
  }>;
  categoriesActive?: Array<number>;
  businesses?: ApiBusiness[];
  page?: number;
  limit?: number;
  total?: number;
  has_more?: boolean;
  message?: string; // Error message from API (e.g., "no_token")
}> {
  const params = new URLSearchParams();
  params.append('language', language);
  if (token) params.append('token', token);
  // Skip 0,0 — PHP empty("0") skips distance calc and returns distance=0.
  const latN = lat != null && lat !== '' ? Number(lat) : NaN;
  const lngN = lng != null && lng !== '' ? Number(lng) : NaN;
  if (Number.isFinite(latN) && Number.isFinite(lngN) && (Math.abs(latN) > 1e-4 || Math.abs(lngN) > 1e-4)) {
    params.append('lat', String(latN));
    params.append('lng', String(lngN));
  }
  if (placeType !== undefined) params.append('place_type', placeType.toString());
  // Add subcategory filter if provided (matches iOS: &subcategory_id=1,2,3)
  if (subcategoryIds && subcategoryIds.length > 0) {
    params.append('subcategory_id', subcategoryIds.join(','));
  }
  const page = options?.page;
  const limit = options?.limit;
  if (page != null && page > 0) params.append('page', String(page));
  if (limit != null && limit > 0) params.append('limit', String(limit));

  const subKey = subcategoryIds && subcategoryIds.length > 0 ? subcategoryIds.slice().sort((a, b) => a - b).join(',') : '';
  const pageKey = limit != null ? `:p${page ?? 1}:l${limit}` : '';
  const cacheKey = `userCategories:${language}:${cacheActorKey(token)}:${roundCoord(lat)}:${roundCoord(lng)}:${placeType ?? ''}:${subKey}${pageKey}`;

  return cachedFetch(
    cacheKey,
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/v1.1/getUserCategories?${params.toString()}`, {
        method: 'GET',
        headers: token
          ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
          : { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to fetch user categories: ${response.statusText}`);
      const data = await response.json();
      if (data.categories) {
        return { ...data, businesses: data.businesses || [] };
      }
      return data;
    },
    {
      ttlMs: TTL.userCategories,
      persist: 'session',
      staleWhileRevalidate: limit == null || (page ?? 1) === 1,
      staleTtlMs: 10 * 60_000,
    }
  );
}

/**
 * Top bookmarked / folder-saved businesses in the user's selected Home categories.
 * Ranked by platform save count. Client should shuffle a subset for Up Next.
 * GET /api/v1.1/upNext?language=&token=&limit=
 */
export async function fetchUpNext(
  token: string,
  language: string = getApiLanguage(),
  limit: number = 40
): Promise<{ businesses: ApiBusiness[]; message?: string }> {
  return cachedFetch(
    `upNext:${language}:${cacheActorKey(token)}:${limit}`,
    async () => {
      const params = new URLSearchParams();
      params.append('token', token);
      params.append('language', language);
      params.append('limit', String(limit));
      const response = await fetch(`${API_BASE_URL}/api/v1.1/upNext?${params.toString()}`, {
        method: 'GET',
        headers: jwtJsonHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as { message?: string }).message || `Failed to fetch up next: ${response.statusText}`);
      }
      return {
        businesses: (data as { businesses?: ApiBusiness[] }).businesses || [],
        message: (data as { message?: string }).message,
      };
    },
    { ttlMs: TTL.upNext, staleWhileRevalidate: true }
  );
}

/**
 * Fetch only user's selected categories (for preferences screen)
 * Matches iOS: /api/v1.1/getOnlyUserCategories?language={lang}&token={token}
 */
export async function fetchOnlyUserCategories(language: string = getApiLanguage(), token?: string): Promise<{
  categories: Array<{ id: number; name: string; [key: string]: any }>;
  subcategories: Array<{ id: number; name: string; category_id?: number; [key: string]: any }>;
  subsubcategories: Array<{ id: number; name: string; category_id?: number; subcategory_id?: number; [key: string]: any }>;
}> {
  return cachedFetch(
    `onlyUserCategories:${language}:${cacheActorKey(token)}`,
    async () => {
      const params = new URLSearchParams();
      params.append('language', language);
      if (token) params.append('token', token);
      const response = await fetch(`${API_BASE_URL}/api/v1.1/getOnlyUserCategories?${params.toString()}`, {
        method: 'GET',
        headers: token
          ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
          : { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Failed to fetch user categories: ${response.statusText}`);
      return response.json();
    },
    {
      ttlMs: TTL.onlyUserCategories,
      persist: 'local',
      staleWhileRevalidate: true,
      staleTtlMs: 60 * 60_000,
    }
  );
}

function bustUserPreferenceCaches(): void {
  invalidateApiCache('userCategories:');
  invalidateApiCache('onlyUserCategories:');
  invalidateApiCache('upNext:');
  invalidateApiCache('categories:');
}

/**
 * Replace user's favourite categories (and all their subs) — iOS Home Okay path.
 * Matches iOS: /api/v1.1/editUserCat?language={lang}&token={token}&categories=id,id,...
 */
export async function updateUserCategories(
  categoryIds: number[],
  language: string = getApiLanguage(),
  token?: string
): Promise<{ message?: string; ids?: string; [key: string]: any }> {
  if (!token) {
    throw new Error('Authentication token required to update preferences');
  }

  const params = new URLSearchParams();
  params.append('language', language);
  params.append('token', token);
  params.append('categories', categoryIds.filter((id) => id > 0).join(','));

  const response = await fetch(`${API_BASE_URL}/api/v1.1/editUserCat?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to update categories: ${response.statusText}`);
  }

  bustUserPreferenceCaches();
  return response.json();
}

/**
 * Update user category preferences by subcategories
 * Matches iOS: /api/v1.1/editUserCatBySubs?language={lang}&token={token}&subcategories={ids}&subsubcategories={ids}
 */
export async function updateUserCategoryPreferences(
  subcategoryIds: number[],
  subsubcategoryIds: number[],
  language: string = getApiLanguage(),
  token?: string
): Promise<{ message?: string; [key: string]: any }> {
  if (!token) {
    throw new Error('Authentication token required to update preferences');
  }

  const params = new URLSearchParams();
  params.append('language', language);
  params.append('token', token);
  params.append('subcategories', subcategoryIds.join(','));
  params.append('subsubcategories', subsubcategoryIds.join(','));

  const url = `${API_BASE_URL}/api/v1.1/editUserCatBySubs?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to update category preferences: ${response.statusText}`);
  }

  bustUserPreferenceCaches();
  return response.json();
}

/**
 * Update user category preferences for a specific category (iOS-style)
 * Matches iOS: /api/v1.1/editUserCatIos?language={lang}&token={token}&category_id={id}&subcategories={ids}&subsubcategories={ids}
 */
export async function updateUserCategoryPreferencesForCategory(
  categoryId: number,
  subcategoryIds: number[],
  subsubcategoryIds: number[],
  language: string = getApiLanguage(),
  token?: string
): Promise<{ message?: string; [key: string]: any }> {
  if (!token) {
    throw new Error('Authentication token required to update preferences');
  }

  const params = new URLSearchParams();
  params.append('language', language);
  params.append('token', token);
  params.append('category_id', categoryId.toString());
  params.append('subcategories', subcategoryIds.join(','));
  if (subsubcategoryIds.length > 0) {
    params.append('subsubcategories', subsubcategoryIds.join(','));
  }

  const url = `${API_BASE_URL}/api/v1.1/editUserCatIos?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to update category preferences: ${response.statusText}`);
  }

  bustUserPreferenceCaches();
  return response.json();
}

// ============================================================================
// AUTHENTICATION APIs
// ============================================================================

/**
 * Register a new user
 * Matches iOS: /api/v1.1/postRegister
 */
export async function checkUserEmailExists(
  email: string,
  language: string = getApiLanguage()
): Promise<{ exists: boolean }> {
  const params = new URLSearchParams();
  params.append('email', email);
  params.append('language', language);

  const dedicated = await fetch(`${API_BASE_URL}/api/v1.1/checkEmail?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (dedicated.ok) {
    const data = (await dedicated.json()) as { exists?: boolean };
    return { exists: Boolean(data.exists) };
  }

  const formData = new FormData();
  formData.append('email', email);
  formData.append('language', language);

  const probe = await fetch(`${API_BASE_URL}/api/v1.1/postRegister`, {
    method: 'POST',
    body: formData,
  });

  let payload: { title?: string; message?: string } = {};
  try {
    payload = (await probe.json()) as { title?: string; message?: string };
  } catch {
    /* ignore */
  }

  if (probe.status === 403) {
    return { exists: true };
  }

  if (probe.status === 400) {
    const combined = `${payload.title || ''} ${payload.message || ''}`.toLowerCase();
    if (
      combined.includes('password') ||
      combined.includes('passwort') ||
      combined.includes('birthday') ||
      combined.includes('geburt')
    ) {
      return { exists: false };
    }
  }

  throw new Error('email_check_failed');
}

export async function registerUser(
  email: string,
  password: string,
  firstName: string,
  birthday: string,
  countryId: number = 1,
  language: string = getApiLanguage(),
  lastName?: string
): Promise<{
  success?: boolean;
  message?: string;
  title?: string;
  token?: string;
  user?: {
    id: number;
    email: string;
    firstName: string;
    lastName?: string;
  };
}> {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('password', password);
  formData.append('firstname', firstName);
  if (lastName) {
    formData.append('lastname', lastName);
  }
  formData.append('birthday', birthday);
  formData.append('country_id', countryId.toString());
  formData.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/postRegister`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Registration failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Request password reset
 * Matches iOS: /api/v1.1/forgot
 */
export async function forgotPassword(
  email: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('email', email);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/forgot?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Password reset request failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Authenticate user (login)
 * Matches iOS: /api/v1.1/authenticate
 */
export async function authenticateUser(
  email: string,
  password: string,
  language: string = getApiLanguage()
): Promise<{
  token?: string;
  user?: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  message?: string;
}> {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('password', password);
  formData.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/authenticate`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Authentication failed: ${response.statusText}`);
  }

  return response.json();
}

export type RefreshAuthTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: string };

/**
 * Refresh JWT (matches iOS home feed: GET /api/v1.1/authenticate/refresh).
 * Sends Bearer + query token — jwt.verify middleware expects the header.
 */
export async function refreshAuthToken(
  token: string,
  language: string = getApiLanguage()
): Promise<RefreshAuthTokenResult> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/authenticate/refresh?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  let data: { token?: string; message?: string } = {};
  try {
    data = (await response.json()) as { token?: string; message?: string };
  } catch {
    /* ignore */
  }

  if (!response.ok) {
    return { ok: false, reason: data.message || response.statusText || 'refresh_failed' };
  }

  const next = typeof data.token === 'string' && data.token.length > 0 ? data.token : null;
  if (!next) {
    return { ok: false, reason: data.message || 'token_missing' };
  }

  return { ok: true, token: next };
}

/** @deprecated Prefer refreshAuthToken */
export async function refreshToken(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  token?: string;
  message?: string;
}> {
  const result = await refreshAuthToken(token, language);
  if (result.ok) {
    return { token: result.token };
  }
  return { message: result.reason };
}

// ============================================================================
// FOLDER/BOOKMARK APIs
// ============================================================================

export interface ApiFolder {
  id: number;
  name: string;
  user_id: number;
  business_count?: number;
  businesses?: ApiBusiness[];
  /** Present on allUserFoldersSorted when sort=new */
  businesses_by_new?: ApiBusiness[];
  /** Present on allUserFoldersSorted when sort=old */
  businesses_by_old?: ApiBusiness[];
  /** Present on allUserFoldersSorted when sort=name */
  businesses_by_name?: ApiBusiness[];
  created_at?: string;
  updated_at?: string;
  time?: string;
}

/** Normalize folder payload from allUserFoldersSorted / businessesFromFolder. */
export function folderBusinesses(folder: ApiFolder | null | undefined): ApiBusiness[] {
  if (!folder) return [];
  return (
    folder.businesses ||
    folder.businesses_by_new ||
    folder.businesses_by_old ||
    folder.businesses_by_name ||
    []
  );
}

/**
 * Get all user folders (sorted)
 * Matches iOS: /api/v1.1/allUserFoldersSorted
 */
export async function fetchUserFolders(
  token: string,
  language: string = getApiLanguage(),
  sort: 'name' | 'new' | 'old' = 'new'
): Promise<{
  folders: ApiFolder[];
  message?: string;
}> {
  return cachedFetch(
    `folders:${language}:${token}:${sort}`,
    async () => {
      const params = new URLSearchParams();
      params.append('token', token);
      params.append('language', language);
      // Backend switch only handles name|new|old — missing/invalid sort → 500
      params.append('sort', sort);
      const response = await fetch(`${API_BASE_URL}/api/v1.1/allUserFoldersSorted?${params.toString()}`, {
        method: 'GET',
        headers: jwtJsonHeaders(token),
      });
      if (!response.ok) throw new Error(`Failed to fetch folders: ${response.statusText}`);
      const data = await response.json();
      const folders = (data.folders || []).map((folder: ApiFolder) => {
        const businesses = folderBusinesses(folder);
        return {
          ...folder,
          businesses,
          business_count: folder.business_count ?? businesses.length,
        };
      });
      return { ...data, folders };
    },
    { ttlMs: TTL.folders, staleWhileRevalidate: true }
  );
}

function bustFolderCaches(): void {
  invalidateApiCache('folders:');
  invalidateApiCache('folderBusinesses:');
}

/**
 * Create a new folder
 * Matches iOS: /api/v1.1/createFolder?name=
 */
export async function createFolder(
  name: string,
  token: string,
  _businessId?: number,
  language: string = getApiLanguage()
): Promise<{
  folder?: ApiFolder;
  title?: string;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('name', name);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/createFolder?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { message?: string }).message || `Failed to create folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return data;
}

/**
 * Delete a folder
 * Matches iOS: /api/v1.1/deleteFolder
 */
export async function deleteFolder(
  folderId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('folder_id', folderId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/deleteFolder?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Rename a folder
 * Matches iOS: /api/v1.1/changeFolderName
 */
export async function renameFolder(
  folderId: number,
  newName: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('folder_id', folderId.toString());
  params.append('folder_name', newName);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/changeFolderName?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to rename folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Get businesses in a folder
 * Matches iOS: /api/v1.1/businessesFromFolder
 */
export async function fetchBusinessesFromFolder(
  folderId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  businesses: ApiBusiness[];
  message?: string;
}> {
  return cachedFetch(
    `folderBusinesses:${language}:${token}:${folderId}`,
    async () => {
      const params = new URLSearchParams();
      params.append('token', token);
      params.append('language', language);
      params.append('folder_id', folderId.toString());
      const response = await fetch(`${API_BASE_URL}/api/v1.1/businessesFromFolder?${params.toString()}`, {
        method: 'GET',
        headers: jwtJsonHeaders(token),
      });
      if (!response.ok) throw new Error(`Failed to fetch businesses from folder: ${response.statusText}`);
      const data = await response.json();
      const businesses = data.businesses || folderBusinesses(data.folder) || [];
      return { ...data, businesses };
    },
    { ttlMs: TTL.folders, staleWhileRevalidate: true }
  );
}

export async function setFolderBusinessIndexes(
  from: number,
  to: number,
  folderId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{ message?: string }> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('from', from.toString());
  params.append('to', to.toString());
  params.append('folder_id', folderId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/setFolderBusinessIndexes?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to reorder folder items: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Add business to folder
 * Matches iOS: /api/v1.1/setBusinessInFolder
 */
export async function addBusinessToFolder(
  businessId: number,
  folderId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('business_id', businessId.toString());
  params.append('folder_id', folderId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/setBusinessInFolder?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to add business to folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Remove business from all folders
 * Matches iOS: /api/v1.1/removeBusinessFromAllFolders
 */
export async function removeBusinessFromAllFolders(
  businessId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('business_id', businessId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/removeBusinessFromAllFolders?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to remove business from folders: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Move businesses to another folder
 * Matches iOS: /api/v1.1/moveBusinessesToFolder
 */
export async function moveBusinessesToFolder(
  businessIds: number[],
  targetFolderId: number,
  token: string,
  language: string = getApiLanguage(),
  oldFolderId?: number
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  // Backend expects `businessIds` + `old_folder_id` (iOS/Android parity)
  params.append('businessIds', businessIds.join(','));
  params.append('folder_id', targetFolderId.toString());
  if (oldFolderId != null) {
    params.append('old_folder_id', oldFolderId.toString());
  }

  const response = await fetch(`${API_BASE_URL}/api/v1.1/moveBusinessesToFolder?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to move businesses to folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

/**
 * Remove businesses from a single folder (organize mode).
 * Matches iOS: /api/v1.1/removeBusinessesFromFolder
 */
export async function removeBusinessesFromFolder(
  businessIds: number[],
  folderId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{ message?: string }> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('folder_id', folderId.toString());
  params.append('businessIds', businessIds.join(','));

  const response = await fetch(`${API_BASE_URL}/api/v1.1/removeBusinessesFromFolder?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to remove from folder: ${response.statusText}`);
  }

  bustFolderCaches();
  return response.json();
}

// ============================================================================
// SEARCH APIs
// ============================================================================

export interface SearchResult {
  businesses: ApiBusiness[];
  categories?: Array<{
    id: number;
    name: string;
    icon?: string;
  }>;
  total?: number;
}

/**
 * Search businesses by string
 * Matches iOS: /api/v1.1/search/searchByString2
 */
export async function searchByString(
  query: string,
  token: string,
  lat?: number,
  lng?: number,
  language: string = getApiLanguage(),
  signal?: AbortSignal
): Promise<SearchResult> {
  const q = query.trim().toLowerCase();
  const key = `search:${language}:${token}:${q}:${roundCoord(lat)}:${roundCoord(lng)}`;
  const hit = peekApiCache<SearchResult>(key);
  if (hit) return hit;

  const fetchOnce = async (): Promise<SearchResult> => {
    const params = new URLSearchParams();
    params.append('token', token);
    params.append('language', language);
    params.append('string', query);
    if (lat !== undefined) params.append('lat', lat.toString());
    if (lng !== undefined) params.append('lng', lng.toString());
    const response = await fetch(`${API_BASE_URL}/api/v1.1/search/searchByString2?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) throw new Error(`Search failed: ${response.statusText}`);
    return response.json();
  };

  if (!signal) {
    return cachedFetch(key, fetchOnce, { ttlMs: TTL.search });
  }

  const result = await fetchOnce();
  if (!signal.aborted) setApiCache(key, result, TTL.search);
  return result;
}

/**
 * Search categories by string
 * Matches iOS: /api/v1.1/search/searchCategoryByString
 */
export async function searchCategoriesByString(
  query: string,
  token: string,
  language: string = getApiLanguage(),
  signal?: AbortSignal
): Promise<{
  categories: Array<{
    id: number;
    name: string;
    icon?: string;
    image?: string;
    color?: string;
    subcategories?: Array<{
      id: number;
      name: string;
    }>;
  }>;
}> {
  type CatSearch = {
    categories: Array<{
      id: number;
      name: string;
      icon?: string;
      image?: string;
      color?: string;
      subcategories?: Array<{ id: number; name: string }>;
    }>;
  };
  const q = query.trim().toLowerCase();
  const key = `searchCategories:${language}:${token}:${q}`;
  const hit = peekApiCache<CatSearch>(key);
  if (hit) return hit;

  const fetchOnce = async (): Promise<CatSearch> => {
    const params = new URLSearchParams();
    params.append('token', token);
    params.append('language', language);
    params.append('string', query);
    const response = await fetch(`${API_BASE_URL}/api/v1.1/search/searchCategoryByString?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) throw new Error(`Category search failed: ${response.statusText}`);
    return response.json();
  };

  if (!signal) {
    return cachedFetch(key, fetchOnce, { ttlMs: TTL.searchCategories });
  }

  const result = await fetchOnce();
  if (!signal.aborted) setApiCache(key, result, TTL.searchCategories);
  return result;
}

/**
 * Advanced search with filters
 * Matches iOS: /api/v1.1/search/advancedSearch
 */
export async function advancedSearch(
  token: string,
  options: {
    query?: string;
    categoryIds?: number[];
    subcategoryIds?: number[];
    lat?: number;
    lng?: number;
    radius?: number; // in km
    placeType?: number;
    sortBy?: 'distance' | 'name' | 'recent';
  },
  language: string = getApiLanguage()
): Promise<SearchResult> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  if (options.query) params.append('string', options.query);
  if (options.categoryIds?.length) params.append('category_id', options.categoryIds.join(','));
  if (options.subcategoryIds?.length) params.append('subcategory_id', options.subcategoryIds.join(','));
  if (options.lat !== undefined) params.append('lat', options.lat.toString());
  if (options.lng !== undefined) params.append('lng', options.lng.toString());
  if (options.radius !== undefined) params.append('radius', options.radius.toString());
  if (options.placeType !== undefined) params.append('place_type', options.placeType.toString());
  if (options.sortBy) params.append('sort_by', options.sortBy);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/search/advancedSearch?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Advanced search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get search history
 * Matches iOS: /api/v1.1/searchHistoryIos
 */
export async function fetchSearchHistory(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  businesses?: ApiBusiness[];
  history?: Array<{
    id: number;
    query?: string;
    business_id?: number;
    business?: ApiBusiness;
    created_at: string;
  }>;
}> {
  return cachedFetch(
    `searchHistory:${language}:${token}`,
    async () => {
      const params = new URLSearchParams();
      params.append('token', token);
      params.append('language', language);
      const response = await fetch(`${API_BASE_URL}/api/v1.1/searchHistoryIos?${params.toString()}`, {
        method: 'GET',
        headers: jwtJsonHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        console.warn('searchHistoryIos failed', response.status, data);
        return { businesses: [], history: [] };
      }
      return data;
    },
    { ttlMs: TTL.searchHistory, staleWhileRevalidate: true }
  );
}

/**
 * Get categories history (recently viewed categories)
 * Matches iOS: /api/v1.1/categoriesHistory
 */
export async function fetchCategoriesHistory(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  categories: Array<{
    id: number;
    name: string;
    icon?: string;
  }>;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/categoriesHistory?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch categories history: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Add business to search history
 * Matches iOS: /api/v1.1/addSearchHistoryBusinessId
 */
export async function addToSearchHistory(
  businessId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('business_id', businessId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/addSearchHistoryBusinessId?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to add to search history: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Clear all search history
 * Matches iOS: /api/v1.1/removeAllHistory
 */
export async function clearSearchHistory(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/removeAllHistory?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to clear search history: ${response.statusText}`);
  }

  invalidateApiCache('searchHistory:');
  return response.json();
}

// ============================================================================
// FAVORITES APIs
// ============================================================================

/**
 * Toggle favorite status for a business
 * Matches iOS: /api/v1.1/favorite/edit?id=<businessId>
 */
export async function toggleFavorite(
  businessId: number,
  _isFavorite: boolean,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  favorite?: number;
  message?: string;
  id?: number;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('id', businessId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/favorite/edit?${params.toString()}`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { message?: string }).message || `Failed to toggle favorite: ${response.statusText}`);
  }

  invalidateApiCache('favorites:');
  invalidateApiCache('upNext:');
  return data;
}

/**
 * Get all favorite businesses
 * Matches iOS: /api/v1.1/favorite?id=<userId>
 */
export async function fetchFavorites(
  token: string,
  lat?: number,
  lng?: number,
  language: string = getApiLanguage(),
  userId?: number
): Promise<{
  businesses: ApiBusiness[];
  message?: string;
}> {
  return cachedFetch(
    `favorites:${language}:${token}:${userId ?? ''}:${roundCoord(lat)}:${roundCoord(lng)}`,
    async () => {
      const params = new URLSearchParams();
      params.append('token', token);
      params.append('language', language);
      if (userId !== undefined) params.append('id', userId.toString());
      if (lat !== undefined) params.append('lat', lat.toString());
      if (lng !== undefined) params.append('lng', lng.toString());
      const response = await fetch(`${API_BASE_URL}/api/v1.1/favorite?${params.toString()}`, {
        method: 'GET',
        headers: jwtJsonHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((data as { message?: string }).message || `Failed to fetch favorites: ${response.statusText}`);
      }
      return data as { businesses: ApiBusiness[]; message?: string };
    },
    { ttlMs: TTL.favorites, staleWhileRevalidate: true }
  );
}

// ============================================================================
// USER PROFILE APIs
// ============================================================================

export interface UserProfile {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  birthday?: string;
  profilePicture?: string;
  country_id?: number;
  language?: string;
  created_at?: string;
  verified_email?: number | boolean;
}

/** Normalize Laravel user payload (firstname/lastname/image) to web UserProfile. */
export function normalizeUserProfile(raw: Record<string, unknown>): UserProfile {
  const pic = raw.profilePicture ?? raw.profile_picture ?? raw.image;
  return {
    id: Number(raw.id),
    email: String(raw.email ?? ''),
    firstName: String(raw.firstName ?? raw.firstname ?? raw.first_name ?? '').trim() || undefined,
    lastName: String(raw.lastName ?? raw.lastname ?? raw.last_name ?? '').trim() || undefined,
    birthday: (raw.birthday as string | undefined) ?? undefined,
    profilePicture: typeof pic === 'string' ? pic : undefined,
    country_id: raw.country_id as number | undefined,
    language: raw.language as string | undefined,
    created_at: raw.created_at as string | undefined,
    verified_email: raw.verified_email as number | boolean | undefined,
  };
}

/**
 * Get user profile
 * Matches iOS: /api/v1.1/user/{id}
 */
export async function fetchUserProfile(
  userId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  user: UserProfile;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/user/${userId}?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user profile: ${response.statusText}`);
  }

  const data = await response.json();
  const rawUser = (data.user ?? data) as Record<string, unknown>;
  return {
    user: normalizeUserProfile(rawUser),
    message: data.message as string | undefined,
  };
}

/**
 * Update user profile
 * Matches iOS: /api/v1.1/updateUserDataPost
 */
export async function updateUserProfile(
  userId: number,
  updates: {
    firstName?: string;
    lastName?: string;
    birthday?: string;
    profilePicture?: File;
  },
  token: string,
  _language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  user?: UserProfile;
  message?: string;
  title?: string;
}> {
  const formData = new FormData();
  formData.append('token', token);
  formData.append('user_id', String(userId));

  if (updates.firstName !== undefined) formData.append('firstname', updates.firstName.trim());
  if (updates.lastName !== undefined && updates.lastName.trim() !== '') {
    formData.append('lastname', updates.lastName.trim());
  }
  if (updates.birthday) {
    const birthdayValue = updates.birthday.includes(' ')
      ? updates.birthday
      : `${updates.birthday} 00:00:00`;
    formData.append('birthday', birthdayValue);
  }
  if (updates.profilePicture) formData.append('image1', updates.profilePicture);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/updateUserDataPost`, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json().catch(() => ({} as Record<string, unknown>));
  const errorTitle = typeof data.title === 'string' ? data.title : '';
  const errorMessage = typeof data.message === 'string' ? data.message : '';
  const isValidationError =
    !response.ok ||
    /invalid input|mindestalter|younger than 16|alert/i.test(`${errorTitle} ${errorMessage}`);

  if (isValidationError) {
    throw new Error(errorMessage || errorTitle || `Failed to update user profile: ${response.statusText}`);
  }

  const rawUser = data.user as Record<string, unknown> | undefined;
  return {
    ...(data as { success?: boolean; message?: string; title?: string }),
    user: rawUser ? normalizeUserProfile(rawUser) : undefined,
  };
}

/**
 * Delete user account (legacy path without reason).
 * Prefer deleteMeProfile for parity with iOS.
 */
export async function deleteUserAccount(
  userId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/user/${userId}/delete?${params.toString()}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete account: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete-account reasons (ME profile).
 * Matches iOS: GET /api/v1.1/allDeleteMeReasons
 */
export async function fetchDeleteMeReasons(
  token: string,
  language: string = getApiLanguage()
): Promise<ReportReason[]> {
  const params = new URLSearchParams({ token, language });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/allDeleteMeReasons?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    reasons?: ReportReason[];
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || response.statusText);
  }
  return data.reasons || [];
}

/**
 * Delete ME account with reason + message (iOS deleteMeProfile flow).
 * GET /api/v1.1/deleteMeProfile?token=&language=&reason_id=&message=
 */
export async function deleteMeProfile(
  reasonId: number | string,
  message: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string }> {
  const params = new URLSearchParams({
    language,
    token,
    reason_id: String(reasonId),
    message,
  });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/deleteMeProfile?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    title?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || data.title || response.statusText);
  }
  return data;
}

/**
 * Businesses owned by the authenticated user (incl. deleted flag server-side).
 * Matches iOS: /api/v1.1/getUserBusinesses
 */
export async function fetchUserBusinesses(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  businesses: ApiBusiness[];
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/getUserBusinesses?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user businesses: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Promoter / company code validation.
 * Matches iOS: /api/v1.1/checkCode?code=…&token=…&language=…
 */
export async function checkPromoterCode(
  code: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  result?: number;
  business?: Record<string, unknown>;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('code', code);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/checkCode?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to validate code: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Sub-businesses linked to a main business.
 * Matches iOS: /api/v1.1/getSubbusinesses
 */
export async function fetchSubbusinesses(
  businessId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  businesses: ApiBusiness[];
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('business_id', businessId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/getSubbusinesses?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch subbusinesses: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Request user data download (GDPR compliance)
 * Matches iOS: /api/v1.1/prepareEmailForJson
 */
export async function requestDataDownload(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/prepareEmailForJson?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to request data download: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// SETTINGS APIs (Billing, Cards, Notifications)
// ============================================================================

export interface BillingAddress {
  id: number;
  name: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  is_default?: boolean;
}

export interface PaymentCard {
  id: number;
  last_four: string;
  brand: string; // visa, mastercard, etc.
  exp_month: number;
  exp_year: number;
  is_default?: boolean;
}

/**
 * Get billing addresses
 * Matches iOS: /api/v1.1/getBillingAddresses
 */
export async function fetchBillingAddresses(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  addresses: BillingAddress[];
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/getBillingAddresses?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  // Prod may not expose this route yet — treat missing endpoint as empty.
  if (response.status === 404) {
    return { addresses: [] };
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch billing addresses: ${response.statusText}`);
  }

  const data = await response.json();
  const raw = (data.billingAddresses ?? data.addresses ?? []) as Array<Record<string, unknown>>;
  const addresses: BillingAddress[] = raw.map((row) => {
    const street =
      String(row.street ?? row.address ?? '') +
      (row.street_number != null && String(row.street_number).length > 0
        ? ` ${row.street_number}`
        : '');
    const nameParts = [row.first_name, row.name, row.company].filter(Boolean).map(String);
    return {
      id: Number(row.id),
      name: nameParts.join(' ').trim() || String(row.name ?? ''),
      street: street.trim(),
      city: String(row.city ?? ''),
      zip: String(row.zip ?? ''),
      country: String(row.country ?? ''),
      is_default: Boolean(row.is_default),
    };
  });
  return { addresses, message: data.message };
}

/**
 * Add billing address
 * Matches iOS: /api/v1.1/addBillingAddress
 */
export async function addBillingAddress(
  address: Omit<BillingAddress, 'id'>,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  address?: BillingAddress;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('token', token);
  formData.append('language', language);
  formData.append('name', address.name);
  formData.append('street', address.street);
  formData.append('city', address.city);
  formData.append('zip', address.zip);
  formData.append('country', address.country);
  if (address.is_default) formData.append('is_default', '1');

  const response = await fetch(`${API_BASE_URL}/api/v1.1/addBillingAddress`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to add billing address: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Remove billing address
 * Matches iOS: /api/v1.1/removeBillingAddress
 */
export async function removeBillingAddress(
  addressId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('address_id', addressId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/removeBillingAddress?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to remove billing address: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get payment cards
 * Matches iOS: /api/v1.1/getCards
 */
export async function fetchPaymentCards(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  cards: PaymentCard[];
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/getCards?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  // Prod may not expose this route yet — treat missing endpoint as empty.
  if (response.status === 404) {
    return { cards: [] };
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch payment cards: ${response.statusText}`);
  }

  const data = await response.json();
  const raw = (data.cards ?? []) as Array<Record<string, unknown>>;
  const cards: PaymentCard[] = raw.map((row) => ({
    id: Number(row.id),
    last_four: String(row.last_four ?? row.last4 ?? row.display_text ?? '').replace(/\D/g, '').slice(-4),
    brand: String(row.brand ?? row.company ?? row.card_brand ?? 'Card'),
    exp_month: Number(row.exp_month ?? row.expMonth ?? 0),
    exp_year: Number(row.exp_year ?? row.expYear ?? 0),
    is_default: Boolean(row.is_default),
  }));
  return { cards, message: data.message };
}

/**
 * Add payment card
 * Matches iOS: /api/v1.1/addCard
 */
export async function addPaymentCard(
  cardToken: string, // Stripe/payment processor token
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  card?: PaymentCard;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('token', token);
  formData.append('language', language);
  formData.append('card_token', cardToken);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/addCard`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to add payment card: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete payment card
 * Matches iOS: /api/v1.1/deleteCard
 */
export async function deletePaymentCard(
  cardId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('card_id', cardId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/deleteCard?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete payment card: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get notification settings
 * Matches iOS notification preferences endpoint
 */
export async function fetchNotificationSettings(
  token: string,
  language: string = getApiLanguage()
): Promise<{
  settings: {
    push_enabled: boolean;
    email_enabled: boolean;
    sms_enabled: boolean;
    marketing_enabled: boolean;
  };
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/getNotificationSettings?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notification settings: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update notification settings
 */
export async function updateNotificationSettings(
  settings: {
    push_enabled?: boolean;
    email_enabled?: boolean;
    sms_enabled?: boolean;
    marketing_enabled?: boolean;
  },
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('token', token);
  formData.append('language', language);

  if (settings.push_enabled !== undefined) formData.append('push_enabled', settings.push_enabled ? '1' : '0');
  if (settings.email_enabled !== undefined) formData.append('email_enabled', settings.email_enabled ? '1' : '0');
  if (settings.sms_enabled !== undefined) formData.append('sms_enabled', settings.sms_enabled ? '1' : '0');
  if (settings.marketing_enabled !== undefined) formData.append('marketing_enabled', settings.marketing_enabled ? '1' : '0');

  const response = await fetch(`${API_BASE_URL}/api/v1.1/updateNotificationSettings`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to update notification settings: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// CATEGORY MANAGEMENT APIs
// ============================================================================

/**
 * Set category as active/inactive
 * Matches iOS: /api/v1.1/setActiveCategory
 */
export async function setActiveCategory(
  categoryId: number,
  active: boolean,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('category_id', categoryId.toString());
  params.append('active', active ? '1' : '0');

  const response = await fetch(`${API_BASE_URL}/api/v1.1/setActiveCategory?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to set category active status: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Remove user category by category ID
 * Matches iOS: /api/v1.1/RemoveUserCatByCatId
 */
export async function removeUserCategory(
  categoryId: number,
  token: string,
  language: string = getApiLanguage()
): Promise<{
  success?: boolean;
  message?: string;
}> {
  const params = new URLSearchParams();
  params.append('token', token);
  params.append('language', language);
  params.append('category_id', categoryId.toString());

  const response = await fetch(`${API_BASE_URL}/api/v1.1/RemoveUserCatByCatId?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to remove user category: ${response.statusText}`);
  }

  return response.json();
}

export type IosPhoneExtras = {
  whatsapp?: boolean;
  signal?: boolean;
  threema?: boolean;
};

/**
 * Append phone fields matching iOS updateBusinessIos payload:
 * phoneN (country+national digits), numberN, countryN, extrasN ("0,0,0" = whatsapp,signal,threema).
 */
export function appendIosPhoneFields(
  fd: FormData,
  rawPhone: string,
  index: 1 | 2 = 1,
  countryDefault = '41',
  extras: IosPhoneExtras = {}
): void {
  const digits = rawPhone.replace(/^\+/, '').replace(/\D/g, '');
  if (!digits) return;
  const national =
    digits.startsWith(countryDefault) && digits.length > countryDefault.length + 5
      ? digits.slice(countryDefault.length)
      : digits;
  const extrasCsv = [
    extras.whatsapp ? '1' : '0',
    extras.signal ? '1' : '0',
    extras.threema ? '1' : '0',
  ].join(',');
  fd.append(`phone${index}`, digits);
  fd.append(`number${index}`, national || digits);
  fd.append(`country${index}`, countryDefault);
  fd.append(`extras${index}`, extrasCsv);
}

/**
 * Create or update a VISI (multipart). Omit business_id on FormData for create.
 * Matches iOS: POST /api/v1.1/updateBusinessIos (JWT Authorization + token field).
 */
export async function postUpdateBusinessIos(
  formData: FormData,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string; business_id?: number }> {
  if (!formData.has('token')) {
    formData.append('token', token);
  }
  if (!formData.has('language')) {
    formData.append('language', language);
  }

  const response = await fetch(`${API_BASE_URL}/api/v1.1/updateBusinessIos`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const data = (await response.json().catch(() => ({}))) as UpdateBusinessIosEnvelope;

  if (isUpdateBusinessIosFailureEnvelope(data, response.ok)) {
    const msg = data.message || data.title || response.statusText;
    throw new Error(msg);
  }

  return data;
}

/**
 * Toggle VISI visibility (active). Matches iOS GET /api/v1.1/updateActiveBusiness.
 */
export async function updateActiveBusiness(
  businessId: number,
  active: boolean,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string }> {
  const params = new URLSearchParams({
    token,
    language,
    business_id: String(businessId),
    active: active ? '1' : '0',
  });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/updateActiveBusiness?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as { title?: string; message?: string };
  if (!response.ok) {
    throw new Error(data.message || data.title || response.statusText);
  }
  if (isSubscriptionLimitTitle(data.title)) {
    throw new Error(data.message || data.title || 'Subscription limit reached.');
  }
  return data;
}

export interface SubscriptionInfoResponse {
  message: string;
  subscription: Record<string, unknown> | null;
  businesses: Array<Record<string, unknown>>;
  codes: Array<Record<string, unknown>>;
}

/**
 * Read-only subscription (App Store; web cannot purchase in browser).
 * GET /api/v1.1/getCurrentSubscription (JWT).
 */
export async function fetchCurrentSubscription(token: string): Promise<SubscriptionInfoResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1.1/getCurrentSubscription`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });
  const data = (await response.json().catch(() => ({}))) as SubscriptionInfoResponse;
  if (!response.ok) {
    throw new Error((data as { message?: string }).message || response.statusText);
  }
  return data;
}

/**
 * Folder / favorite activity feed.
 * GET /api/v1.1/newsByCreatedAt (JWT).
 */
export async function fetchNewsByCreatedAt(token: string): Promise<{
  news?: Array<Record<string, unknown>>;
  message?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/v1.1/newsByCreatedAt`, {
    method: 'GET',
    headers: jwtJsonHeaders(token),
  });
  return response.json() as Promise<{ news?: Array<Record<string, unknown>> }>;
}

export type SupportMessageType = 'problem' | 'request';

export interface SupportCategory {
  id: string;
  name: string;
}

export async function fetchSupportCategories(
  type: SupportMessageType,
  token: string,
  language: string = getApiLanguage()
): Promise<SupportCategory[]> {
  const params = new URLSearchParams({ type, language, token });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/supportCategories?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    categories?: SupportCategory[];
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || response.statusText);
  }
  return data.categories || [];
}

export async function submitSupportMessage(
  type: SupportMessageType,
  category: string,
  message: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string }> {
  const path = type === 'request' ? 'submitRequest' : 'reportProblem';
  const params = new URLSearchParams({
    language,
    token,
    category,
    message,
  });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/${path}?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    title?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || data.title || response.statusText);
  }
  return data;
}

export async function changePassword(
  password: string,
  newPassword: string,
  newPasswordConfirm: string,
  token: string
): Promise<{ title?: string; message?: string }> {
  const formData = new FormData();
  formData.append('password', password);
  formData.append('new_password', newPassword);
  formData.append('new_password_confirm', newPasswordConfirm);
  formData.append('token', token);

  const response = await fetch(`${API_BASE_URL}/api/v1.1/changepass`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  });
  const data = (await response.json().catch(() => ({}))) as {
    title?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || data.title || response.statusText);
  }
  return data;
}

export interface ReportReason {
  id: number;
  name: string;
  name_de?: string;
}

export async function fetchReportReasons(
  token: string,
  language: string = getApiLanguage()
): Promise<ReportReason[]> {
  const params = new URLSearchParams({ token, language });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/allReasons?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    reasons?: ReportReason[];
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || response.statusText);
  }
  // Reason model hides `id` in JSON; iOS uses 1-based list index (btnReason.tag+1)
  return (data.reasons || []).map((r, i) => ({
    ...r,
    id: typeof r.id === 'number' && r.id > 0 ? r.id : i + 1,
  }));
}

export async function reportBusiness(
  businessId: number,
  reasonId: number,
  message: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string }> {
  const params = new URLSearchParams({
    token,
    language,
    business_id: String(businessId),
    reason_id: String(reasonId),
    message,
  });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/reportBusiness?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    title?: string;
    message?: string;
  };
  const msg = `${data.message || ''} ${data.title || ''}`.toLowerCase();
  // 400 + already reported is a successful idempotent path (matches iOS / e2e)
  if (!response.ok && !msg.includes('already')) {
    throw new Error(data.message || data.title || response.statusText);
  }
  return data;
}

export async function fetchDeleteVisiReasons(
  token: string,
  language: string = getApiLanguage()
): Promise<ReportReason[]> {
  const params = new URLSearchParams({ token, language });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/allDeleteReasons?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    reasons?: ReportReason[];
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || response.statusText);
  }
  return data.reasons || [];
}

export async function deleteVisiBusiness(
  businessId: number,
  reasonId: number | string,
  message: string,
  token: string,
  language: string = getApiLanguage()
): Promise<{ title?: string; message?: string }> {
  const params = new URLSearchParams({
    language,
    token,
    business_id: String(businessId),
    reason_id: String(reasonId),
    message,
  });
  const response = await fetch(`${API_BASE_URL}/api/v1.1/updateDeletedBusiness?${params}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as {
    title?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.message || data.title || response.statusText);
  }
  return data;
}

export interface SocialTypeItem {
  /** Absent in practice: the backend hides `id` on SocialType, so match on `name`. */
  id?: number;
  name: string;
  icon?: string;
}

export async function fetchSocialTypes(token: string): Promise<SocialTypeItem[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1.1/allSocials?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  const data = (await response.json().catch(() => ({}))) as { socials?: SocialTypeItem[] };
  if (!response.ok) {
    throw new Error('Failed to load social types');
  }
  return data.socials || [];
}
